import React, { FC, useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { IntakeFormStepLayout } from "../components";
import { useIntakeForm } from "../context";
import { useGisSessionOps } from "../useGisSessionOps";

const messages = defineMessages({
  title: {
    id: "intakeStepClinicInfo.title",
    defaultMessage: "Enter your clinic information",
  },
  orderingPhysicianLabel: {
    id: "intakeStepClinicInfo.orderingPhysicianLabel",
    defaultMessage: "Name of Your Ordering Physician",
  },
  orderingPhysicianPlaceholder: {
    id: "intakeStepClinicInfo.orderingPhysicianPlaceholder",
    defaultMessage: "Enter first name & last name",
  },
  physicianClinicLabel: {
    id: "intakeStepClinicInfo.physicianClinicLabel",
    defaultMessage: "Name of Your Physician’s Clinic",
  },
  physicianClinicPlaceholder: {
    id: "intakeStepClinicInfo.physicianClinicPlaceholder",
    defaultMessage: "Enter name of clinic",
  },
  nextButton: {
    id: "intakeStepClinicInfo.nextButton",
    defaultMessage: "Next",
  },
});

const MAX_FIELD_LENGTH = 255;

interface IntakeStepClinicInfoFormProps {
  persistPrefilledData?: (data: {
    orderingPhysician: string;
    physicianClinic: string;
  }) => Promise<void> | void;
}

export const IntakeStepClinicInfoForm: FC<IntakeStepClinicInfoFormProps> = ({
  persistPrefilledData,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    prefilledData,
    updatePrefilledField,
    setCurrentStep,
    steps,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.CLINIC_INFO;
  const [orderingPhysician, setOrderingPhysician] = useState(
    prefilledData?.orderingPhysician ?? ""
  );
  const [physicianClinic, setPhysicianClinic] = useState(
    prefilledData?.physicianClinic ?? ""
  );
  const [isPersisting, setIsPersisting] = useState(false);

  useEffect(() => {
    if (steps.includes(currentStep)) {
      setCurrentStep(currentStep);
    }
  }, [setCurrentStep, steps]);

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleOrderingPhysicianChange = (value: string) => {
    setOrderingPhysician(value);
    updatePrefilledField("orderingPhysician", value);
  };

  const handlePhysicianClinicChange = (value: string) => {
    setPhysicianClinic(value);
    updatePrefilledField("physicianClinic", value);
  };

  const isContinueDisabled =
    !orderingPhysician.trim() || !physicianClinic.trim() || isPersisting;

  const handleContinue = async () => {
    if (isContinueDisabled) return;

    const trimmedData = {
      orderingPhysician: orderingPhysician.trim(),
      physicianClinic: physicianClinic.trim(),
    };
    updatePrefilledField("orderingPhysician", trimmedData.orderingPhysician);
    updatePrefilledField("physicianClinic", trimmedData.physicianClinic);
    setIsPersisting(true);
    try {
      await persistPrefilledData?.(trimmedData);
    } catch (error) {
      console.error("Failed to persist manually entered clinic data:", error);
    }
    setIsPersisting(false);
    navigate(buildRoute(getNextStep(currentStep)));
  };

  if (!steps.includes(currentStep)) {
    return <Navigate to={buildRoute(null)} replace />;
  }

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={() => navigate(buildRoute(getPreviousStep(currentStep)))}
      continueDisabled={isContinueDisabled}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__field-group">
        <label
          className="intake-form-step__field-label"
          htmlFor="ordering-physician"
        >
          {intl.formatMessage(messages.orderingPhysicianLabel)}
          <span className="intake-form-step__field-label__required"> *</span>
        </label>
        <input
          id="ordering-physician"
          name="orderingPhysician"
          className="intake-form-step__text-input intake-form-step__text-input--clinic"
          value={orderingPhysician}
          onChange={(event) =>
            handleOrderingPhysicianChange(event.target.value)
          }
          placeholder={intl.formatMessage(
            messages.orderingPhysicianPlaceholder
          )}
          maxLength={MAX_FIELD_LENGTH}
          required
        />
      </div>

      <div className="intake-form-step__field-group">
        <label
          className="intake-form-step__field-label"
          htmlFor="physician-clinic"
        >
          {intl.formatMessage(messages.physicianClinicLabel)}
          <span className="intake-form-step__field-label__required"> *</span>
        </label>
        <input
          id="physician-clinic"
          name="physicianClinic"
          className="intake-form-step__text-input intake-form-step__text-input--clinic"
          value={physicianClinic}
          onChange={(event) => handlePhysicianClinicChange(event.target.value)}
          placeholder={intl.formatMessage(messages.physicianClinicPlaceholder)}
          maxLength={MAX_FIELD_LENGTH}
          autoComplete="organization"
          required
        />
      </div>
    </IntakeFormStepLayout>
  );
};

const IntakeStepClinicInfo: FC = () => {
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const { getActiveSession, updateStep } = useGisSessionOps();

  const persistPrefilledData = useCallback(
    async (data: { orderingPhysician: string; physicianClinic: string }) => {
      if (!orderUidPublic || !testUid) return;
      const session = await getActiveSession({ orderUidPublic, testUid });
      if (session?.id == null) return;
      await updateStep({
        sessionId: session.id,
        input: {},
        prefilledInput: data,
      });
    },
    [getActiveSession, orderUidPublic, testUid, updateStep]
  );

  return (
    <IntakeStepClinicInfoForm persistPrefilledData={persistPrefilledData} />
  );
};

export default IntakeStepClinicInfo;
